/**
 * Mission Briefing — shown after the city has loaded, before the round begins.
 * Gives the player a beat to see where they're being deployed (city + country)
 * and decide to commit with an explicit "BEGIN MISSION" click. Previously the
 * game screen mounted immediately on city-load complete, which felt jarring
 * once R2-served cities load in ~1s.
 *
 * Visual style matches LoadingScreen deliberately — same classification stripe,
 * same city-name treatment — so the transition feels like the next page of the
 * same briefing dossier rather than a new screen.
 */

import { theme } from '../../styles/theme';

interface MissionBriefingScreenProps {
  /** Short city name (e.g. "Dublin"). Used for the large display. */
  cityName: string;
  /** Full "City, Country" form if known. We parse the country out. Pass the
   * short name alone when country isn't known (practice mode, legacy save). */
  displayName?: string;
  /** Current run round, or null for practice mode. */
  round?: number | null;
  onBegin: () => void;
}

export function MissionBriefingScreen({ cityName, displayName, round = null, onBegin }: MissionBriefingScreenProps) {
  // displayName format is "City, Country" — split on the last comma to handle
  // city names that contain commas (none currently, but defensive).
  const country = (() => {
    if (!displayName) return null;
    const idx = displayName.lastIndexOf(',');
    if (idx === -1) return null;
    return displayName.slice(idx + 1).trim();
  })();

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.classification}>
          TOP SECRET // <span style={{ color: '#ffdd44' }}>{round != null ? `ROUND ${round}` : 'PRACTICE'}</span>
        </div>
        <h2 style={styles.title}>MISSION BRIEFING</h2>
        <div style={styles.cityName}>{cityName.toUpperCase()}</div>
        {country && <div style={styles.country}>{country.toUpperCase()}</div>}

        <div style={styles.divider} />

        <div style={styles.flavorText}>
          <div style={styles.flavorLine}>SIGNAL SECURED · ASSET IN POSITION</div>
          <div style={styles.flavorLine}>STANDBY FOR DEPLOYMENT ORDERS</div>
        </div>

        <button
          style={styles.beginButton}
          onClick={onBegin}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(68, 204, 136, 0.18)';
            e.currentTarget.style.boxShadow = `0 0 18px ${theme.colors.accentGreen}55`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(68, 204, 136, 0.08)';
            e.currentTarget.style.boxShadow = `0 0 0px ${theme.colors.accentGreen}40`;
          }}
        >
          BEGIN MISSION
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.colors.background,
  },
  content: {
    textAlign: 'center',
    maxWidth: '480px',
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  classification: {
    fontSize: '11px',
    letterSpacing: '3px',
    color: theme.colors.accentRed,
    marginBottom: '24px',
    fontWeight: 500,
  },
  title: {
    fontSize: '14px',
    letterSpacing: '6px',
    color: theme.colors.textSecondary,
    marginBottom: '8px',
    fontWeight: 400,
  },
  cityName: {
    fontSize: '36px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    letterSpacing: '6px',
    marginBottom: '6px',
    fontFamily: theme.fonts.display,
  },
  country: {
    fontSize: '13px',
    letterSpacing: '4px',
    color: theme.colors.textSecondary,
    marginBottom: '40px',
  },
  divider: {
    width: '120px',
    height: '1px',
    background: theme.colors.border,
    marginBottom: '32px',
  },
  flavorText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '48px',
  },
  flavorLine: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
  },
  beginButton: {
    fontFamily: theme.fonts.mono,
    fontSize: '13px',
    letterSpacing: '4px',
    fontWeight: 600,
    color: theme.colors.accentGreen,
    background: 'rgba(68, 204, 136, 0.08)',
    border: `1px solid ${theme.colors.accentGreen}`,
    padding: '14px 36px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxShadow: `0 0 0px ${theme.colors.accentGreen}40`,
  },
};
